import { CHAPTERS } from "../lib/data";

/**
 * ChapterHero — art-directed cinematic hero for each chapter, designed as if
 * it were the opening spread of a premium children's picture book. The
 * typography placement is composed against each unique hero image rather than
 * pinned to the same template slot.
 *
 * Props:
 *   - path       Route path used to look up the chapter number/label.
 *   - image      Absolute URL to the hero webp.
 *   - title      Optional override for the on-hero title.
 *   - eyebrow    Optional override for the small "Chapter N" eyebrow.
 *   - subtitle   Optional single line of scripty subtitle text.
 *   - position   Where the title lockup sits on the hero. One of:
 *                 "top-left" | "top-center" | "top-right"
 *                 "middle-left" | "middle-center" | "middle-right"
 *                 "bottom-left" | "bottom-center" | "bottom-right"
 *                 Defaults to "middle-center".
 *   - textColor  Optional CSS color for the title (defaults to warm cream).
 */
const POSITIONS = {
  "top-left":      { justify: "flex-start", align: "flex-start", text: "text-left"   },
  "top-center":    { justify: "flex-start", align: "center",     text: "text-center" },
  "top-right":     { justify: "flex-start", align: "flex-end",   text: "text-right"  },
  "middle-left":   { justify: "center",     align: "flex-start", text: "text-left"   },
  "middle-center": { justify: "center",     align: "center",     text: "text-center" },
  "middle-right":  { justify: "center",     align: "flex-end",   text: "text-right"  },
  "bottom-left":   { justify: "flex-end",   align: "flex-start", text: "text-left"   },
  "bottom-center": { justify: "flex-end",   align: "center",     text: "text-center" },
  "bottom-right":  { justify: "flex-end",   align: "flex-end",   text: "text-right"  },
};

export default function ChapterHero({
  path,
  image,
  title,
  eyebrow,
  subtitle,
  position = "middle-center",
  size = "md",               // "md" (default) or "sm" — shrinks the full lockup one step
  textColor = "#4B3A2F",     // walnut brown — matches nav + headings; painted-on feel
  eyebrowColor,               // optional distinct color for the small eyebrow
  subtitleColor,              // optional distinct color for the script subtitle
  logoSrc,
  testId,
  contentStyle,               // optional inline style overrides on the inner lockup wrapper
  subtitleStyle,              // optional inline style overrides on the subtitle <p>
  titleAlt,                   // plain-text fallback used for alt when `title` is a React node
  overlay,                    // optional JSX rendered above the hero image (below the title lockup)
  titleBackdrop,              // optional JSX (e.g., an img) rendered INSIDE the h1, behind the title text
  titleGraphic,               // if provided, REPLACES the default eyebrow/title/subtitle lockup with custom JSX
  titleClassName = "",        // optional extra className appended to the <h1>
}) {
  const chapter = CHAPTERS.find((c) => c.path === path);
  const chapterLabel = title || chapter?.label || "";
  const chapterLabelAlt = titleAlt
    || (typeof chapterLabel === "string" ? chapterLabel.replace(/\s*\n\s*/g, " ") : "");
  const chapterEyebrow = eyebrow || (chapter?.short ? `Chapter ${chapter.short}` : "");
  const p = POSITIONS[position] || POSITIONS["middle-center"];
  const eyebrowInk = eyebrowColor || textColor;
  const subtitleInk = subtitleColor || "#2A1B10";  // darker chocolate for subtitle by default
  const isSm = size === "sm";
  const titleFontSize = isSm
    ? "clamp(1.6rem, 4.2vw, 3.6rem)"
    : "clamp(2rem, 5.2vw, 4.6rem)";
  const subtitleFontSize = isSm
    ? "clamp(0.9rem, 1.55vw, 1.4rem)"
    : "clamp(1.05rem, 1.9vw, 1.75rem)";
  const logoHeight = isSm
    ? "clamp(2rem, 4.8vw, 4.4rem)"
    : "clamp(2.5rem, 6vw, 5.5rem)";

  return (
    <section
      className="relative w-full overflow-hidden"
      data-testid={testId || `chapter-hero-${path.replace(/\//g, "") || "home"}`}
      style={{
        aspectRatio: "16 / 7",
        maxHeight: "72vh",
        backgroundColor: "#2B1C12",
      }}
    >
      <img
        src={image}
        alt={chapterLabelAlt}
        className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
        draggable="false"
      />

      {overlay}

      {/* gentle bottom vignette — road continuing into parchment */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, rgba(247,240,228,0.35) 55%, #F7F0E4 100%)",
        }}
      />

      {/* soft top haze so the top-nav stays readable */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-24 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(43,28,18,0.35) 0%, transparent 100%)",
        }}
      />

      {/* Art-directed title lockup — either the default eyebrow/title/
          subtitle stack or a fully custom `titleGraphic` (e.g., the
          Salt Block chapter marker where the chapter text is engraved
          onto the salt block image itself). */}
      <div
        className="absolute inset-x-0 top-0 flex flex-col px-6 md:px-12 lg:px-16 pt-4 md:pt-6 lg:pt-8"
        style={{
          alignItems: p.align,
        }}
      >
        <div className={`sky-heading-animate max-w-2xl ${p.text}`} style={contentStyle}>
          {titleGraphic ? (
            titleGraphic
          ) : (
            <>
              {chapterEyebrow && (
                <p
                  className="text-xs md:text-sm uppercase tracking-[0.42em]"
                  style={{
                    color: eyebrowInk,
                    textShadow:
                      "0 2px 8px rgba(255,241,214,0.85), 0 0 12px rgba(255,241,214,0.7)",
                  }}
                >
                  {chapterEyebrow}
                </p>
              )}
              <h1
                className={`font-display leading-[0.95] mt-1 relative inline-block ${titleClassName}`}
                style={{
                  fontSize: titleFontSize,
                  color: textColor,
                  whiteSpace: "pre-line",
                  textShadow:
                    "0 3px 18px rgba(255,241,214,0.95), 0 0 14px rgba(255,241,214,0.85), 0 0 4px rgba(255,241,214,0.9)",
                }}
              >
                {titleBackdrop}
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt={chapterLabelAlt}
                    className="inline-block w-auto select-none"
                    draggable="false"
                    style={{
                      height: logoHeight,
                      filter: "drop-shadow(0 3px 14px rgba(255,241,214,0.85))",
                    }}
                  />
                ) : (
                  <span className="relative" style={{ zIndex: 1 }}>{chapterLabel}</span>
                )}
              </h1>
              {subtitle && (
                <p
                  className="font-script mt-0.5"
                  style={{
                    fontSize: subtitleFontSize,
                    color: subtitleInk,
                    textShadow:
                      "0 3px 12px rgba(255,241,214,0.9), 0 0 10px rgba(255,241,214,0.75)",
                    ...(subtitleStyle || {}),
                  }}
                >
                  {subtitle}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
